import React from 'react';
import { User, Shield, Edit2, Trash2, UserPlus } from 'lucide-react';
import type { InnerCircleMember } from '../../types/inner-circle';

interface MemberListProps {
  members: InnerCircleMember[];
  onInviteMember: () => void;
  onUpdateRole: (memberId: string, role: InnerCircleMember['role']) => void;
  onRemoveMember: (memberId: string) => void;
  isAdmin: boolean;
}

export function MemberList({
  members,
  onInviteMember,
  onUpdateRole,
  onRemoveMember,
  isAdmin,
}: MemberListProps) {
  const getRoleColor = (role: InnerCircleMember['role']) => {
    switch (role) {
      case 'admin':
        return 'text-red-500';
      case 'editor':
        return 'text-yellow-500';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-4 mt-6 lg:mt-0">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Members</h3>
        {isAdmin && (
          <button
            onClick={onInviteMember}
            className="p-2 hover:bg-black/20 rounded-lg transition-colors"
          >
            <UserPlus className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="space-y-2">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-3 bg-black/20 rounded-xl flex-wrap gap-2"
          >
            <div className="flex items-center gap-3">
              {member.profile?.avatar_url ? (
                <img
                  src={member.profile.avatar_url}
                  alt={member.profile.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[var(--accent-color)] flex items-center justify-center">
                  <User className="w-5 h-5 text-black" />
                </div>
              )}
              <div>
                <h4 className="font-medium">
                  {member.profile?.name || member.profile?.email}
                </h4>
                <div className="flex items-center gap-2 text-sm">
                  <Shield className={`w-4 h-4 ${getRoleColor(member.role)}`} />
                  <span className="capitalize text-gray-400">{member.role}</span>
                </div>
              </div>
            </div>

            {isAdmin && member.role !== 'admin' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const newRole =
                      member.role === 'viewer' ? 'editor' : 'viewer';
                    onUpdateRole(member.id, newRole);
                  }}
                  className="p-2 hover:bg-black/20 rounded-lg transition-colors"
                  title={`Change to ${
                    member.role === 'viewer' ? 'editor' : 'viewer'
                  }`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onRemoveMember(member.id)}
                  className="p-2 hover:bg-black/20 rounded-lg transition-colors text-red-500"
                  title="Remove member"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}